/** «Подходящие знания» + «Применения методов» on a task — SPEC-2 §3.1–3.3. */

import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { BookOpen } from "lucide-react";
import { Link } from "react-router-dom";

import { Applications, Knowledge, Methods } from "@/api/resources";
import type { MethodApplicationOut } from "@/api/types";
import { Badge } from "@/components/ui/Badge";
import { Button } from "@/components/ui/Button";
import { Card } from "@/components/ui/Card";
import { Section } from "@/components/ui/Section";
import { SkeletonList } from "@/components/ui/Skeleton";
import { useToast } from "@/components/ui/Toast";
import { SuggestionCard } from "@/components/SuggestionCard";
import { TaskReportSheet } from "@/components/TaskReportSheet";
import { haptics } from "@/lib/telegram-ui";

/* ── Подходящие знания ────────────────────────────────────────────────── */

export function TaskSuggestions({ taskId, version }: { taskId: string; version?: number }) {
  const qc = useQueryClient();
  const toast = useToast();
  const suggestions = useQuery({
    queryKey: ["suggestions", taskId, version],
    refetchInterval: 60_000,
    queryFn: () => Knowledge.suggestions(taskId),
  });

  const apply = useMutation({
    mutationFn: (methodId: string) => Applications.start(taskId, { method_id: methodId }),
    onSuccess: () => {
      haptics.success();
      qc.invalidateQueries({ queryKey: ["applications", taskId] });
      toast.success("Применение начато — отчёт попросим при закрытии");
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const dismiss = useMutation({
    mutationFn: (variables: { type: Parameters<typeof Knowledge.dismiss>[1]; id: string }) =>
      Knowledge.dismiss(taskId, variables.type, variables.id),
    onSuccess: () => {
      haptics.tap();
      qc.invalidateQueries({ queryKey: ["suggestions", taskId] });
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const refresh = useMutation({
    mutationFn: () => Knowledge.suggestions(taskId, true),
    onSuccess: (data) => qc.setQueryData(["suggestions", taskId, version], data),
    onError: (e: Error) => toast.error(e.message),
  });

  if (suggestions.isLoading) return <SkeletonList count={2} />;
  return (
    <Section
      title="Подходящие знания"
      trailing={
        <Button size="compact" variant="ghost" loading={suggestions.isFetching || refresh.isPending} onClick={() => refresh.mutate()}>Обновить</Button>
      }
    >
      {suggestions.isError && <p role="alert" className="text-meta text-dstr">Не удалось загрузить подсказки. Нажми «Обновить».</p>}
      {!suggestions.isError && suggestions.data?.length === 0 && <p className="text-meta text-hint">Подходящих знаний пока нет.</p>}
      {(suggestions.data ?? []).map((hit) => (
        <SuggestionCard
          key={`${hit.entity_type}-${hit.entity_id}`}
          hit={hit}
          applying={apply.isPending}
          onApply={
            hit.entity_type === "method" ? () => apply.mutate(hit.entity_id) : undefined
          }
          onDismiss={() => dismiss.mutate({ type: hit.entity_type, id: hit.entity_id })}
        />
      ))}
    </Section>
  );
}

/* ── Применения методов ───────────────────────────────────────────────── */

export function TaskApplications({ taskId }: { taskId: string }) {
  const [reporting, setReporting] = useState<MethodApplicationOut | null>(null);
  const applications = useQuery({
    queryKey: ["applications", taskId],
    queryFn: () => Applications.forTask(taskId),
  });

  if (applications.isLoading) return <SkeletonList count={1} />;
  if (applications.isError || !applications.data?.length) return null;

  return (
    <>
      <Section title="Применения методов">
        {applications.data.map((a) => (
          <ApplicationCard key={a.id} application={a} onReport={() => setReporting(a)} />
        ))}
      </Section>
      {reporting && (
        <TaskReportSheet
          taskId={taskId}
          applications={[reporting]}
          onClose={() => setReporting(null)}
        />
      )}
    </>
  );
}

function ApplicationCard({
  application,
  onReport,
}: {
  application: MethodApplicationOut;
  onReport: () => void;
}) {
  const method = useQuery({
    queryKey: ["method", application.method_id],
    queryFn: () => Methods.get(application.method_id),
  });
  const versionNumber = method.data?.versions.find((v) => v.id === application.version_id)?.number;
  const open = application.outcome === "in_progress";

  return (
    <Card className="space-y-2">
      <div className="flex items-start gap-2">
        <BookOpen className="mt-0.5 h-[18px] w-[18px] shrink-0 text-hint" aria-hidden="true" />
        <div className="min-w-0 flex-1">
          <Link to={`/knowledge/${application.method_id}`} className="text-cell text-text">
            {method.data?.title ?? "Метод"}
          </Link>
          <p className="text-meta text-hint">
            {versionNumber ? `v${versionNumber}` : "версия"}
          </p>
        </div>
        <Badge
          tone={
            application.outcome === "success"
              ? "accent"
              : application.outcome === "failed"
                ? "danger"
                : "neutral"
          }
        >
          {application.outcome}
        </Badge>
      </div>
      {application.result_md && (
        <p className="text-meta text-hint">{application.result_md}</p>
      )}
      {open && (
        <Button size="compact" variant="secondary" onClick={onReport}>
          Отчёт
        </Button>
      )}
    </Card>
  );
}
